import { FC, useEffect, useMemo, useState } from "react"
import classNames from "classnames"

// components
import { UpperNavMicroCopy } from "../../Header/Header"

// icons
import { CalendarIcon, CheckOutIcon, GearIcon } from "@eventbrite/marmalade"

// utils
import { getBaseUrl } from "../../../utils/general_utils"
import { renderRichText } from "../../../utils/contentful/render_rich_text"

// types
import { UserProfile } from "../../../store/slices/user_profile.slice"

// styles
import * as globalStyles from "../../../theme/global.module.scss"
import * as styles from "./ProfileMenuBarDropdown.module.scss"

type SingleListItemProps = {
  href?: string
  children?: React.ReactNode
  isHidden?: boolean
  isDivider?: boolean
  onClick?: React.MouseEventHandler<HTMLAnchorElement>
}

export type Props = {
  isOpen: boolean
  handleLogin: () => Promise<void>
  userProfile: UserProfile
  microCopy: UpperNavMicroCopy | null
}

// @TODO: need to architect what this data looks like coming from Contentful and refactor
export const ProfileMenuBarDropdown: FC<Props> = ({
  isOpen,
  handleLogin,
  userProfile,
  microCopy
}) => {
  const userId = useMemo(
    () => userProfile?.allData?.userAuthData?.id || "",
    [userProfile]
  )
  const numberOfTickets = useMemo(
    () => userProfile?.allData?.userOrderData?.orders?.length || 0,
    [userProfile]
  )
  const returnPath = useMemo(() => {
    if (typeof window !== "undefined") {
      return window?.location?.pathname
        ? encodeURIComponent(window?.location?.pathname)
        : ""
    }

    return ""
  }, [])

  const [hideBrowseEvents, setHideBrowseEvents] = useState(false)

  useEffect(() => {
    const path = window.location.pathname
    const onHelpPage = /^\/help(\/[a-z]{2}(-[a-z]{2})?)?\/?$/.test(path)
    const onMobile = window.innerWidth <= 570
    setHideBrowseEvents(onHelpPage && onMobile)
  }, [])

  const menuItems: SingleListItemProps[] = useMemo(
    () => [
      {
        href: `${
          getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
        }/d/local/all-events/`,
        children: renderRichText(microCopy?.navigationBrowseEvents?.raw),
        isHidden: hideBrowseEvents
      },
      {
        isHidden: !userId || userId?.length <= 0,
        href: `${
          getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
        }/u/${userId}/`,
        children: (
          <>
            {renderRichText(microCopy?.navigationTickets?.raw)} (
            {numberOfTickets})
          </>
        )
      },
      {
        isDivider: true
      },
      {
        href: `${
          getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
        }/organizations/home`,
        children: (
          <>
            <span className={styles.menuIcon}>
              <CalendarIcon size="small" />
            </span>
            {renderRichText(microCopy?.navigationManageMyEvents?.raw)}
          </>
        )
      },
      {
        href: `${
          getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
        }/account-settings/`,
        children: (
          <>
            <span className={styles.menuIcon}>
              <GearIcon size="small" />
            </span>
            {renderRichText(microCopy?.navigationAccountSettings?.raw)}
          </>
        )
      },
      {
        isDivider: true
      },
      {
        onClick: handleLogin,
        href: `${
          getBaseUrl() ? getBaseUrl() : process.env.GATSBY_PRODUCT_SITE_URL
        }/logout?referrer=${returnPath}`,
        children: (
          <>
            <span className={styles.menuIcon}>
              <CheckOutIcon size="small" />
            </span>
            <div>
              {renderRichText(microCopy?.navigationLogOut?.raw)}
              {userProfile?.email}
            </div>
          </>
        )
      }
    ],
    [userProfile, userId, numberOfTickets, hideBrowseEvents]
  )

  return (
    <div
      className={classNames(
        globalStyles.edsDropdown,
        globalStyles.edsFxFadeIn,
        globalStyles.edsDropdownAlignRight,
        globalStyles.edsDropdownDropDown,
        styles.profileMenuBarDropdownContainer,
        {
          [styles.hidden]: !isOpen
        }
      )}
      tabIndex={-1}
    >
      <ul className={globalStyles.edsNavList}>
        {menuItems?.map((item) => {
          return (
            <SingleListItem
              key={(item?.href?.length ?? 0) > 0 ? item.href : Math.random()}
              href={item.href}
              isDivider={item.isDivider}
              isHidden={item.isHidden}
              onClick={item.onClick}
            >
              {item?.children ? item.children : null}
            </SingleListItem>
          )
        })}
      </ul>
    </div>
  )
}

const SingleListItem: FC<SingleListItemProps> = ({
  children,
  href,
  isHidden,
  onClick,
  isDivider
}) => {
  if (isHidden) {
    return null
  }

  if (isDivider) {
    return (
      <li className={globalStyles.edsNavListItem} data-spec="eds-nav-list-item">
        <hr
          className={styles.navDivider}
          data-spec="divider-hr"
          aria-hidden="true"
        />
      </li>
    )
  }

  return (
    <li className={globalStyles.edsNavListItem} data-spec="eds-nav-list-item">
      <a
        onClick={onClick ? onClick : () => null}
        tabIndex={-1}
        href={href}
        data-spec="anchor-list-item"
        className={classNames(
          globalStyles.edsNavListItem,
          globalStyles.edsNavListItemLink,
          styles.profileMenuLink
        )}
      >
        <div
          className={classNames(
            globalStyles.edsListItem,
            globalStyles.edsListItemAlignMiddle
          )}
          data-spec="eds-list-item"
        >
          <div
            className={globalStyles.edsListItemContents}
            data-spec="eds-list-item-contents"
          >
            <div
              className={classNames(
                globalStyles.edsNavListItemContents,
                styles.singleMenuItem
              )}
            >
              {children}
            </div>
          </div>
        </div>
      </a>
    </li>
  )
}
